import {
  createContext,
  useCallback,
  useContext,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { cn } from "@/lib/cn";

type Tone = "info" | "success" | "error";
interface ToastItem {
  id: number;
  tone: Tone;
  message: string;
  /** Marks the leaving phase so we can play the slide-out animation. */
  leaving?: boolean;
}

interface Ctx {
  toast: (message: string, tone?: Tone) => void;
}

const Toaster = createContext<Ctx | null>(null);

const toneStyles: Record<Tone, string> = {
  info: "border-line bg-surface-2 text-foreground",
  success: "border-good/40 bg-good/10 text-good",
  error: "border-bad/40 bg-bad/10 text-bad",
};

const toneIcons: Record<Tone, string> = {
  info: "M12 16v-4M12 8h.01",
  success: "m5 12 5 5L20 7",
  error: "m9 9 6 6m0-6-6 6",
};

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);
  const idRef = useRef(0);

  const toast = useCallback((message: string, tone: Tone = "info") => {
    const id = ++idRef.current;
    setItems((prev) => {
      // Cheap dedup: if the same message+tone is already on screen, skip.
      if (prev.some((t) => t.message === message && t.tone === tone && !t.leaving)) {
        return prev;
      }
      return [...prev, { id, tone, message }];
    });
    const visibleMs = tone === "error" ? 6000 : 3500;
    // Phase 1: mark leaving so the slide-out plays
    window.setTimeout(() => {
      setItems((prev) =>
        prev.map((t) => (t.id === id ? { ...t, leaving: true } : t)),
      );
    }, visibleMs);
    // Phase 2: actually unmount after the leaving animation finishes
    window.setTimeout(() => {
      setItems((prev) => prev.filter((t) => t.id !== id));
    }, visibleMs + 220);
  }, []);

  /** Manual dismiss — same two-phase exit as the timeout path. The
   *  pending timeouts become no-ops once the item is gone. */
  const dismiss = useCallback((id: number) => {
    setItems((prev) =>
      prev.map((t) => (t.id === id ? { ...t, leaving: true } : t)),
    );
    window.setTimeout(() => {
      setItems((prev) => prev.filter((t) => t.id !== id));
    }, 220);
  }, []);

  return (
    <Toaster.Provider value={{ toast }}>
      {children}
      <div className="pointer-events-none fixed bottom-4 right-4 z-[60] flex flex-col gap-2">
        {items.map((t) => (
          <div
            key={t.id}
            role="status"
            aria-live="polite"
            className={cn(
              "pointer-events-auto flex min-w-[18rem] max-w-md items-start gap-3 " +
                "rounded-md border px-3 py-2.5 text-sm shadow-elev backdrop-blur-md",
              toneStyles[t.tone],
              t.leaving
                ? "translate-x-6 opacity-0 transition-all duration-200 ease-in"
                : "animate-toast-in",
            )}
          >
            <svg
              className="mt-0.5 h-4 w-4 shrink-0"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={1.8}
              aria-hidden
            >
              {t.tone === "info" ? (
                <>
                  <circle cx={12} cy={12} r={9} />
                  <path d={toneIcons[t.tone]} strokeLinecap="round" />
                </>
              ) : t.tone === "success" ? (
                <path d={toneIcons[t.tone]} strokeLinecap="round" strokeLinejoin="round" />
              ) : (
                <>
                  <circle cx={12} cy={12} r={9} />
                  <path d={toneIcons[t.tone]} strokeLinecap="round" />
                </>
              )}
            </svg>
            <span className="flex-1 leading-relaxed">{t.message}</span>
            <button
              type="button"
              onClick={() => dismiss(t.id)}
              aria-label="Dismiss notification"
              className="-mr-1 shrink-0 rounded p-0.5 opacity-60 transition hover:opacity-100"
            >
              <svg
                className="h-4 w-4"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={1.8}
                aria-hidden
              >
                <path d="M6 6l12 12M18 6 6 18" strokeLinecap="round" />
              </svg>
            </button>
          </div>
        ))}
      </div>
    </Toaster.Provider>
  );
}

export function useToast() {
  const ctx = useContext(Toaster);
  if (!ctx) throw new Error("useToast must be used inside <ToastProvider>");
  return ctx;
}
